/**
 * ChatInput Component (Shared)
 *
 * Input area for composing and sending messages.
 * Features auto-expanding textarea, attachment (image OR document), reply
 * preview bar, and enter to send.
 *
 * #451: the composer used to hard-reject anything that was not JPG/PNG, so
 * PDFs, Word/Excel files, etc. could never be shared through chat even though
 * `chat_messages.message_type` already had a `'file'` value and `attachments`
 * is a generic JSONB array. Documents are now accepted, previewed as a named
 * chip (a PDF does not render inside an `<img>`), and rendered by
 * `MessageBubble` as a download link.
 *
 * @module components/chat/ChatInput
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FiSend, FiX, FiPaperclip } from 'react-icons/fi';
import { CHAT_ACCEPTED_TYPES, CHAT_FILE_LABELS, FILE_UPLOAD } from '../../utils/constants';

/**
 * Exported for tests. Returns `{ ok: true }` or `{ ok: false, error }` — the
 * single place that decides what the composer accepts, so the `accept`
 * attribute and the runtime check can never disagree.
 */
export const validateChatFile = (file) => {
  if (!file) return { ok: false, error: 'No se seleccionó ningún archivo' };
  if (!CHAT_ACCEPTED_TYPES.includes(file.type)) {
    return {
      ok: false,
      error: 'Tipo de archivo no permitido. Se aceptan imágenes (JPG, PNG, WEBP, GIF) y documentos (PDF, Word, Excel, PowerPoint, CSV, TXT, ZIP).',
    };
  }
  if (file.size > FILE_UPLOAD.MAX_SIZE) {
    return { ok: false, error: 'El archivo no puede exceder 10MB' };
  }
  return { ok: true, error: null };
};

/** Human-readable size for the attachment chip. */
export const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SharedChatInput = ({
  onSend,
  disabled = false,
  placeholder = 'Escribe un mensaje...',
  sending = false,
  replyTo = null,
  onCancelReply,
}) => {
  const [message, setMessage] = useState('');
  // #451: was `imageFile`/`imagePreview`. Now holds any accepted attachment;
  // `filePreview` is only populated for images (documents get a chip instead).
  const [attachedFile, setAttachedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [error, setError] = useState(null);

  const isImageAttachment = !!attachedFile?.type?.startsWith('image/');
  const fileMeta = attachedFile ? CHAT_FILE_LABELS[attachedFile.type] : null;

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 100);
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [message, adjustTextareaHeight]);

  // Focus textarea when replying
  useEffect(() => {
    if (replyTo && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [replyTo]);

  const handleInputChange = (e) => {
    setMessage(e.target.value);
    setError(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const { ok, error: validationError } = validateChatFile(file);
    if (!ok) {
      setError(validationError);
      // Reset so picking the SAME rejected file again still fires `onChange`.
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setAttachedFile(file);
    setError(null);

    // Only images get a thumbnail — a PDF read as a data URL cannot render in
    // an <img>, which is exactly the bug #451 warns about.
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFilePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const removeAttachment = () => {
    setAttachedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSend = () => {
    if (sending || disabled) return;

    const trimmedMessage = message.trim();
    if (!trimmedMessage && !attachedFile) return;

    onSend(trimmedMessage, attachedFile);

    setMessage('');
    removeAttachment();
    setError(null);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const canSend = (message.trim().length > 0 || !!attachedFile) && !sending && !disabled;

  return (
    <>
      {/* Reply Preview Bar */}
      {replyTo && (
        <div className="reply-preview-bar">
          <div className="reply-preview-content">
            <span className="reply-preview-sender">
              {replyTo.sender?.full_name || 'Usuario'}
            </span>
            <span className="reply-preview-text">
              {replyTo.content?.substring(0, 80) || ''}
              {replyTo.content?.length > 80 ? '...' : ''}
            </span>
          </div>
          <button
            type="button"
            className="reply-preview-close"
            onClick={onCancelReply}
            aria-label="Cancelar respuesta"
          >
            <FiX size={16} />
          </button>
        </div>
      )}

      {/* Image preview (thumbnail) */}
      {filePreview && isImageAttachment && (
        <div className="image-preview-container">
          <div className="image-preview">
            <img src={filePreview} alt="Vista previa" />
            <button
              type="button"
              className="image-preview-remove"
              onClick={removeAttachment}
              aria-label="Eliminar adjunto"
            >
              <FiX size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Document preview (#451): a named chip — documents cannot render as <img> */}
      {attachedFile && !isImageAttachment && (
        <div className="image-preview-container">
          <div className="file-preview-chip">
            <i className={`bi ${fileMeta?.icon || 'bi-file-earmark'} file-preview-icon`} aria-hidden="true" />
            <span className="file-preview-info">
              <span className="file-preview-name" title={attachedFile.name}>
                {attachedFile.name}
              </span>
              <span className="file-preview-meta">
                {[fileMeta?.label, formatFileSize(attachedFile.size)].filter(Boolean).join(' · ')}
              </span>
            </span>
            <button
              type="button"
              className="file-preview-remove"
              onClick={removeAttachment}
              aria-label="Eliminar adjunto"
            >
              <FiX size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="image-preview-container">
          <small className="text-danger" role="alert">{error}</small>
        </div>
      )}

      {/* Input Area */}
      <div className="chat-input-container">
        <div className="chat-input-wrapper">
          <button
            type="button"
            className="chat-attach-btn"
            onClick={triggerFileSelect}
            disabled={disabled || sending}
            aria-label="Adjuntar archivo"
            title="Adjuntar imagen o documento"
          >
            <FiPaperclip size={22} />
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept={CHAT_ACCEPTED_TYPES.join(',')}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            data-testid="chat-file-input"
          />

          <textarea
            ref={textareaRef}
            className="chat-input-field"
            value={message}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled || sending}
            rows={1}
            aria-label="Mensaje"
          />
        </div>

        <button
          type="button"
          className="chat-send-btn"
          onClick={handleSend}
          disabled={!canSend}
          aria-label="Enviar mensaje"
        >
          {sending ? (
            <div className="message-loading-spinner" style={{ width: 20, height: 20 }} />
          ) : (
            <FiSend size={20} />
          )}
        </button>
      </div>
    </>
  );
};

SharedChatInput.propTypes = {
  onSend: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  sending: PropTypes.bool,
  replyTo: PropTypes.shape({
    id: PropTypes.string,
    content: PropTypes.string,
    sender: PropTypes.shape({
      full_name: PropTypes.string,
    }),
  }),
  onCancelReply: PropTypes.func,
};

export default SharedChatInput;
